// Spark purchase — Stripe hosted checkout, run entirely on the user's own
// account through the browser SDK, the same way this app renders. There is no
// server step: the Sogni platform owns the Stripe session, the prices, and the
// crediting; this app only forwards the buyer to checkout and polls the result.
// Mirrors sogni-web's Purchase/spark flow — GET products, POST purchase for a
// hosted checkout URL, GET status to poll — against the same platform APIs.

import { getClient, APP_SOURCE } from './sogni.js';

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const sparkFmt = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });

// Turn the platform's raw Stripe prices into product cards. Pure so the
// discount/sort/label math can be tested without a network or a client.
//
// Discount is each pack's per-Spark price against the WORST per-Spark price on
// offer, so the biggest pack reads as the best value — the same framing
// sogni-web uses. Anything without a positive spark value and a numeric amount
// is dropped rather than shown as a broken, unpurchasable card.
export function normalizeSparkProducts(raw) {
  const priced = (Array.isArray(raw) ? raw : [])
    .map((p) => {
      const spark = Number(p?.metadata?.sparkValue);
      const amount = Number(p?.unit_amount);
      return Number.isFinite(spark) && spark > 0 && Number.isFinite(amount) && amount > 0
        ? { p, spark, amount, perSpark: amount / spark }
        : null;
    })
    .filter(Boolean);

  const worstPerSpark = priced.reduce((max, x) => Math.max(max, x.perSpark), 0);

  return priced
    .sort((a, b) => a.amount - b.amount)
    .map(({ p, spark, amount, perSpark }) => ({
      // Checkout wants the product id, not the price id.
      id: p.product,
      spark,
      sparkLabel: spark < 1000 ? String(spark) : `${sparkFmt.format(spark / 1000)}K`,
      price: amount / 100,
      priceLabel: usd.format(amount / 100),
      description: String(p?.metadata?.localDescription || p?.nickname || ''),
      discount:
        worstPerSpark > 0 ? Math.round(((worstPerSpark - perSpark) / worstPerSpark) * 100) : 0
    }));
}

// The Spark packs on offer, cheapest first, best-value flagged.
export async function listSparkProducts() {
  const client = await getClient();
  const res = await client.apiClient.rest.get('/v1/iap/stripe/products');
  return normalizeSparkProducts(res?.data?.products);
}

export function estimateTessellationCount(spark, sparkPerTessellation) {
  const balance = Number(spark);
  const cost = Number(sparkPerTessellation);
  if (!Number.isFinite(balance) || balance <= 0 || !Number.isFinite(cost) || cost <= 0) return null;
  return Math.floor(balance / cost);
}

// Create a hosted Stripe checkout session for a product. Returns the URL to
// send the buyer to and the id to poll. `redirectType: 'web'` tells the
// platform to use its web success/cancel pages.
export async function startSparkPurchase(productId) {
  const client = await getClient();
  const res = await client.apiClient.rest.post('/v1/iap/stripe/purchase', {
    productId,
    redirectType: 'web',
    appSource: APP_SOURCE
  });
  const data = res?.data || {};
  if (!data.url || !data.purchaseId) throw new Error('checkout could not be started');
  return { url: data.url, purchaseId: data.purchaseId };
}

// Poll a purchase. 'initiated' means payment is not in yet; 'processing' and
// 'completed' both mean the Spark has been credited (advance credit lands at
// 'processing'); 'expired' means the session died before payment.
export async function getSparkPurchase(purchaseId) {
  const client = await getClient();
  const res = await client.apiClient.rest.get(`/v1/iap/status/${encodeURIComponent(purchaseId)}`);
  return res?.data || null;
}

export function isPurchaseCredited(status) {
  return status === 'processing' || status === 'completed';
}
export function isPurchaseDead(status) {
  return status === 'expired';
}
